<template>
  <edit-module
    ref="editModule"
    :saving="saving"
    :disabled="!hasChanges"
    @submit="handleSubmit"
    @expand="toggleOpen"
  >
    <template #header>
      <div>
        <b>Status: </b>
        {{ displayAppointmentStatus(appointment.status) || "--" }}
      </div>
    </template>
    <template #body>
      <div v-if="open">
        <div class="text-bold">Status</div>
        <Select
          v-model="formData.status"
          class="mt-2 mb-2"
          :options="statusOptions"
          name="Status"
          no-label
          :field-error="firstErrorMessage(v$.formData?.status)"
        ></Select>
        <component
          :is="actualTimesFormVersion"
          v-if="hasChanges"
          class="actual-times-form p-2"
          :appt-state="formData.status"
          :accepted-time="formData.accepted_time"
          :dispatch-time="formData.dispatch_time"
          :arrival-time="formData.arrival_time"
          :departure-time="formData.departure_time"
          :scheduled-time="appointment.scheduled_time"
          :lost-times="lostTimes"
          :disabled-fields="disabledTimeFields"
          :job-id="jobId"
          @change="handleTimeChange"
        >
          <template #label>
            <div class="font-bold mb-3">
              Actual Times Need to be Set to Correct the Schedule Board and
              Account for Efficiency
            </div>
          </template>
        </component>
        <text-area
          v-if="hasChanges"
          v-model="formData.reason"
          label="Reason for Status Change"
          class="full-width mt-2"
          name="reason"
          :validation-errors="v$.$errors"
        ></text-area>
        <change-summary
          :changes="changes"
          :original="originalValues"
        ></change-summary>
      </div>
    </template>
  </edit-module>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { getObjDiff, getProperty } from "@/lib/helpers.js";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import EditModule from "@/components/EditModule.vue";
import TextArea from "@/components/TextArea.vue";
import CONSTANTS from "@/Constants.js";
import ActualTimesFormV2 from "@/EditAppointmentModal/forms/ActualTimesFormV2.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import ChangeSummary from "../../ChangeSummary.vue";
const {
  APPOINTMENT_STATUS_MAP,
  APPOINTMENT_STATUS_ORDER,
  APPOINTMENT_STATUS_TIME_LOSSES,
  APPOINTMENT_STATUS_TIME_KEYS
} = CONSTANTS;

export default {
  components: {
    EditModule,
    ChangeSummary,
    ActualTimesFormV2,
    TextArea
  },
  props: {
    appointment: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      formData: {
        status: null,
        accepted_time: "",
        dispatch_time: "",
        arrival_time: "",
        departure_time: "",
        reason: ""
      },
      saving: false,
      open: false
    };
  },
  computed: {
    ...mapGetters(["featureEnabledForTenant"]),
    changes() {
      const changedData = { ...this.formData };
      this.lostTimes.forEach((time) => {
        changedData[time] = "Lost";
        if (time === "accepted_time") {
          changedData.preWork = "Lost";
        }
        if (time === "arrival_time") {
          changedData.travel = "Lost";
        }
        if (time === "departure_time") {
          changedData.work = "Lost";
        }
        if (time === "departure_time") {
          changedData.duration = "Lost";
        }
      });
      return getObjDiff(this.originalValues, changedData);
    },
    originalValues() {
      const {
        arrival_time,
        dispatch_time,
        departure_time,
        status,
        accepted_time
      } = this.appointment;
      return {
        arrival_time,
        dispatch_time,
        accepted_time,
        departure_time,
        status,
        preWork: "",
        travel: "",
        work: "",
        duration: ""
      };
    },
    statusOptions() {
      const availableStatuses = [this.appointment.status].concat(
        Object.keys(this.appointment.links)
      );
      // Sort statuses by flow
      const sortedStatuses = APPOINTMENT_STATUS_ORDER.filter((status) =>
        availableStatuses.includes(status)
      );
      return sortedStatuses
        .filter((status) => APPOINTMENT_STATUS_MAP[status])
        .map((status) => ({
          value: status,
          label: APPOINTMENT_STATUS_MAP[status]
        }));
    },
    hasChanges() {
      return Object.keys(this.changes).length > 0;
    },
    statusLink() {
      return this.appointment.links[this.formData.status];
    },
    lostTimes() {
      const possibleLostFields =
        APPOINTMENT_STATUS_TIME_LOSSES[this.appointment.status][
          this.formData.status
        ] || [];
      // Only return lost field warning if the field has a value
      return possibleLostFields.filter((field) => this.formData[field]);
    },
    disabledTimeFields() {
      if (!this.statusLink) {
        return [];
      }
      const {
        PARAM_ARRIVAL_TIME,
        PARAM_DEPARTURE_TIME,
        PARAM_DISPATCH_TIME,
        PARAM_ACCEPTED_TIME
      } = APPOINTMENT_STATUS_TIME_KEYS;
      const fields = [
        PARAM_ACCEPTED_TIME,
        PARAM_DISPATCH_TIME,
        PARAM_ARRIVAL_TIME,
        PARAM_DEPARTURE_TIME
      ];
      const { parameters } = this.statusLink;
      const requiredFields = Object.keys(parameters);
      return fields.filter(
        (field) => !requiredFields.includes(field) || this.appointment[field]
      );
    },
    jobId() {
      return getProperty(this.appointment.job, "id");
    },
    actualTimesFormVersion() {
      return ActualTimesFormV2;
    }
  },
  watch: {
    open(newValue) {
      if (!newValue) {
        this.resetForm();
      }
    },
    statusLink(newValue) {
      if (!newValue) {
        this.resetForm();
      }
    }
  },
  created() {
    this.resetForm();
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      formData: {
        status: { required, $autoDirty: true },
        reason: { required, $autoDirty: true }
      }
    };
  },
  methods: {
    ...mapActions(["updateAppointmentStatus"]),
    getRequestParams() {
      const params = {
        reason: this.formData.reason
      };
      const { parameters } = this.statusLink;
      const paramKeys = Object.keys(parameters);
      paramKeys.forEach((paramKey) => {
        // Replace the special param value keys with the correct
        // formData key name
        const valueKey = APPOINTMENT_STATUS_TIME_KEYS[parameters[paramKey]];
        const value = this.formData[valueKey];
        if (value) {
          params[paramKey] = value;
        }
      });
      return params;
    },
    handleSubmit() {
      this.v$.$validate().then((isValid) => {
        if (isValid) {
          if (this.statusLink) {
            this.saving = true;
            const params = this.getRequestParams();
            this.updateAppointmentStatus({ link: this.statusLink, params })
              .then(() => {
                window.notify.success("Appointment updated successfully!");
                this.$refs.editModule?.handleClose();
              })
              .finally(() => (this.saving = false));
          } else {
            window.notify.error(
              "Unable to edit the status of this appointment."
            );
          }
        }
      });
    },
    handleTimeChange(name, value) {
      this.formData[name] = value;
    },
    resetForm() {
      const {
        status,
        dispatch_time,
        arrival_time,
        departure_time,
        accepted_time
      } = this.appointment;
      this.formData = {
        status,
        dispatch_time,
        arrival_time,
        departure_time,
        accepted_time
      };
    },
    toggleOpen() {
      this.open = !this.open;
    },
    firstErrorMessage
  }
};
</script>

<style lang="scss" scoped>
.actual-times-form {
  border-radius: 3px;
}
</style>
